/**
 * AppShell — sidebar (Arbeit / Übersicht / Einstellungen) + topbar, per the
 * designed IA (Story UI-U2, decision Valerio 2026-07-27; nav model in nav.ts).
 * SPA nav via TanStack <Link>; active state from the router location.
 *
 * P1b: collapsible sidebar (keyboard shortcut Ctrl/Cmd+B, persisted to localStorage).
 * ADMIN-W4: the user block renders the LOGGED-IN identity from the broker
 * session (ID-token claims, display only) + an Abmelden action (Keycloak
 * end-session). The hard-coded "Lara Meier" placeholder is gone.
 * UI-U2: honest count badges (useNavCounts — badge only when a real total
 * exists), ⌘K focuses the global SearchBar, org header shows the REAL tenant
 * name + pricing-tier label (useTenantSelf; NO workspace switcher — decision
 * 2026-07-27), topbar back/forward wired to real router history (Kap. 25).
 */
import { type ReactNode, useState, useEffect } from 'react';
import { Link, useCanGoBack, useRouter, useRouterState } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Moon, Sun, LifeBuoy, MessageSquarePlus, PanelLeftClose, PanelLeftOpen,
  ChevronLeft, ChevronRight, LogOut, Upload,
} from 'lucide-react';
import type { DocExtractionKind, MeProfileResponse } from '@brokly/shared';
import { useTheme } from '../providers/theme-provider';
import { cn } from '../../lib/cn';
import { GlobalSearchDialog, HeaderSearchTrigger } from '../../features/search/GlobalSearchDialog';
import { CountBadge } from '../../components/ui/CountBadge';
import { isMac } from '../../components/ui/Kbd';
import { loadBrokerSession } from '../../lib/auth/auth-session';
import { logout } from '../../features/auth/useBrokerAuth';
import { useEffectivePermissions } from '../../features/administration/useEffectivePermissions';
import { hasAnyAdminKey } from '../../features/administration/adminNav';
import { NAV_SECTIONS, isActive, titleFor, type NavItem, type NavSection } from './nav';
import { useNavCounts, type NavCounts } from './useNavCounts';
import { useTenantSelf } from './useTenantSelf';
import { apiClient, API_BASE_URL } from '../../lib/api-client';
import { qk } from '../../lib/query-keys';
import { isLiveData } from '../../lib/data-mode';
import { BrokRay } from '../../features/brok/components/BrokRay';
import { NotificationBell } from '../../features/notifications/NotificationBell';
import { UploadDocumentDialog } from '../../features/documents/components/UploadDocumentDialog';
import { Toast } from '../../components/ui/Toast';

/**
 * Footer profile avatar: the picture uploaded on the Einstellungen page (Part C),
 * falling back to text initials when none is set. Shares the `qk.meProfile()` query
 * key with EinstellungenScreen, so a fresh upload there is reflected here immediately
 * from cache — no extra round trip.
 */
function ProfileAvatar({
  avatarUrl,
  initials,
  size = 'size-7',
}: {
  avatarUrl: string | null | undefined;
  initials: string;
  size?: string;
}) {
  if (avatarUrl) {
    return <img src={avatarUrl} alt="" className={cn(size, 'rounded-full object-cover')} />;
  }
  return (
    <div
      className={cn(
        'grid place-items-center rounded-full bg-primary/15 text-xs font-semibold text-primary',
        size,
      )}
    >
      {initials}
    </div>
  );
}

const SIDEBAR_STORAGE_KEY = 'brokly-sidebar';

/**
 * Org-header mandate logo (UI-U2 AC 5): renders the mandate's own broker logo from
 * the PUBLIC branding endpoint (GET /public/branding/:tenantId/logo), degrading to
 * the "B" brand box on load error OR when no tenant id is known yet (pre-login /
 * mock without an id). Mirrors BrandingPreview's Wordmarkable img→fallback pattern.
 */
export function MandateLogo({ tenantId, name }: { tenantId?: string | null; name: string }) {
  const [failed, setFailed] = useState(false);
  // Reset the error latch whenever the tenant (→ logo URL) changes.
  useEffect(() => setFailed(false), [tenantId]);

  if (tenantId && !failed) {
    return (
      <img
        src={`${API_BASE_URL}/public/branding/${tenantId}/logo`}
        alt={name}
        onError={() => setFailed(true)}
        className="size-7 shrink-0 rounded-md object-contain"
      />
    );
  }
  return (
    <div className="grid size-7 shrink-0 place-items-center rounded-md bg-primary text-xs font-bold text-primary-foreground">
      B
    </div>
  );
}

function NavLink({
  item,
  path,
  collapsed,
  count,
}: {
  item: NavItem;
  path: string;
  collapsed: boolean;
  count: number | undefined;
}) {
  const Icon = item.icon;
  const active = isActive(item.to, path);
  return (
    <Link
      to={item.to}
      aria-current={active ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
        active
          ? 'bg-sidebar-accent text-sidebar-accent-foreground'
          : 'text-sidebar-foreground/80 hover:bg-sidebar-accent/60 hover:text-sidebar-foreground',
        collapsed && 'justify-center px-2',
      )}
      title={collapsed ? item.label : undefined}
    >
      {/* Icons one step quieter than labels (design.md §5); active = the exclusive signal. */}
      <Icon className={cn('size-4 shrink-0', !active && 'text-sidebar-foreground/60')} />
      {!collapsed && (
        <>
          <span className="flex-1">{item.label}</span>
          {/* Honest or absent (AC 3): badge ONLY when a real count exists; hidden collapsed. */}
          {count !== undefined && <CountBadge count={count} active={active} />}
        </>
      )}
    </Link>
  );
}

function Section({
  section,
  path,
  collapsed,
  counts,
  className,
}: {
  section: NavSection;
  path: string;
  collapsed: boolean;
  counts: NavCounts;
  className?: string;
}) {
  return (
    <div className={className}>
      {!collapsed && (
        <div className="px-3 pb-1 text-xs font-semibold uppercase tracking-wider text-sidebar-foreground/40">
          {section.title}
        </div>
      )}
      <div className="space-y-0.5">
        {section.items.map((it) => (
          <NavLink
            key={it.to}
            item={it}
            path={path}
            collapsed={collapsed}
            count={it.badgeKey ? counts[it.badgeKey] : undefined}
          />
        ))}
      </div>
    </div>
  );
}

/**
 * True when an in-app history entry exists ahead of the current one. Relies on TanStack's
 * internal `__TSR_index` (not history.length, which counts pre-app + login entries); tested.
 * A push truncates forward entries, so the tip resets to the current index on push.
 */
function useCanGoForward(): boolean {
  const router = useRouter();
  const [canGoForward, setCanGoForward] = useState(false);
  useEffect(() => {
    let tip = router.history.location.state.__TSR_index;
    const recompute = (index: number, isPush: boolean) => {
      tip = isPush ? index : Math.max(tip, index);
      setCanGoForward(index < tip);
    };
    recompute(router.history.location.state.__TSR_index, false);
    return router.history.subscribe(({ location, action }) =>
      recompute(location.state.__TSR_index, action.type === 'PUSH'),
    );
  }, [router]);
  return canGoForward;
}

export function AppShell({ children }: { children: ReactNode }) {
  const { theme, toggle } = useTheme();
  const router = useRouter();
  const canGoBack = useCanGoBack();
  const canGoForward = useCanGoForward();
  const path = useRouterState({ select: (s) => s.location.pathname });
  // Display-only identity from the broker session (ADMIN-W4; null pre-login in tests).
  const session = loadBrokerSession();
  // Org header data (UI-U2 AC 5): real tenant name + tier; null → brand fallback.
  const tenant = useTenantSelf();
  // Footer avatar (Part C): same query key as EinstellungenScreen's GET /me/profile,
  // so a freshly uploaded avatar shows here immediately from the shared cache.
  // `enabled: live` keeps mock/test runs network-free (falls back to initials, as before).
  const live = isLiveData();
  const profileQuery = useQuery<MeProfileResponse>({
    queryKey: qk.meProfile(),
    queryFn: () => apiClient.me.profile(),
    enabled: live,
    staleTime: 60_000,
  });
  const avatarUrl = profileQuery.data?.avatar_url ?? null;
  // Honest count badges (UI-U2 AC 3): absent key = no badge.
  const counts = useNavCounts();
  // UI-U6: gate the EXISTING UI-U2 "Administration" nav entry (do NOT add a second
  // one) — it shows only when the caller can reach ≥1 admin page (server truth via
  // GET /me/effective-permissions; the api guards remain the hard line). In mock
  // mode the wildcard fixture grants it, so the pre-U6 nav tests stay green.
  const adminAccess = useEffectivePermissions();
  const showAdmin = hasAnyAdminKey(adminAccess);
  const navSections = showAdmin
    ? NAV_SECTIONS
    : NAV_SECTIONS.map((s) => ({ ...s, items: s.items.filter((it) => it.to !== '/admin') })).filter(
        (s) => s.items.length > 0,
      );
  // C2: the header search opens a centered popup over the blurred page.
  const [searchOpen, setSearchOpen] = useState(false);
  // S5: the global Upload button opens the shared UploadDocumentDialog from any page.
  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadToast, setUploadToast] = useState<'ok' | 'dup' | null>(null);
  const qc = useQueryClient();

  function showUploadToast(kind: 'ok' | 'dup') {
    setUploadToast(kind);
    setTimeout(() => setUploadToast(null), 6000);
  }

  // S5: the global upload — same contract as the Ablage page's handleUpload (per-file), so
  // the shared dialog works unchanged. Invalidates the Ablage list + surfaces a toast,
  // including the dedup verdict so a byte-identical re-upload is not a silent no-op.
  async function handleGlobalUpload(
    file: File,
    opts: { docKind?: DocExtractionKind; title?: string },
  ) {
    if (!isLiveData()) {
      showUploadToast('ok');
      return { document_id: 'mock-doc', job_id: 'mock-job', status: 'queued' as const };
    }
    const res = await apiClient.documents.upload(file, { filename: file.name, ...opts });
    await qc.invalidateQueries({ queryKey: ['documents', 'list'] });
    showUploadToast(res.dedup_status === 'duplicate' ? 'dup' : 'ok');
    return res;
  }

  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem(SIDEBAR_STORAGE_KEY) === 'collapsed',
  );

  // Persist collapse state
  useEffect(() => {
    localStorage.setItem(SIDEBAR_STORAGE_KEY, collapsed ? 'collapsed' : 'expanded');
  }, [collapsed]);

  // Keyboard shortcuts: Ctrl/Cmd+B = collapse (P1b, unchanged) ·
  // Ctrl/Cmd+K = OPEN the global search popup (C2). It used to focus the sidebar input,
  // which no longer exists; the shortcut is the same one the header control advertises.
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'b') {
        e.preventDefault();
        setCollapsed((c) => !c);
        return;
      }
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        e.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  const toggleSidebar = () => setCollapsed((c) => !c);
  // Label states the action; title also shows the Ctrl/Cmd+B shortcut per platform.
  const sidebarToggleLabel = collapsed ? 'Seitenleiste ausklappen' : 'Seitenleiste einklappen';
  const sidebarToggleTitle = `${sidebarToggleLabel} (${isMac() ? '⌘B' : 'Strg+B'})`;

  return (
    <div className="flex h-screen w-full overflow-hidden bg-background text-foreground">
      <aside
        id="app-sidebar"
        data-state={collapsed ? 'collapsed' : 'expanded'}
        aria-label="Seitenleiste"
        className={cn(
          'flex shrink-0 flex-col border-r border-sidebar-border bg-sidebar transition-[width]',
          collapsed ? 'w-16' : 'w-64',
        )}
      >
        {/* Org header — REAL tenant name + pricing-tier label (UI-U2 AC 5).
            NO workspace switcher/dropdown (decision Valerio 2026-07-27). */}
        <div className={cn('flex items-center gap-2 px-4 py-4', collapsed && 'justify-center px-2')}>
          <MandateLogo tenantId={tenant?.id} name={tenant?.name ?? 'Brokly'} />
          {!collapsed && (
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-semibold leading-none text-sidebar-foreground">
                {tenant?.name ?? 'Brokly'}
              </div>
              {tenant?.pricing_tier_label != null && (
                <div className="mt-0.5 truncate text-[10px] text-sidebar-foreground/50">
                  {tenant.pricing_tier_label}
                </div>
              )}
            </div>
          )}
        </div>

        {/* C2 (wave 5): the sidebar search is GONE. There is now exactly ONE search entry,
            in the top header to the left of the KI-Assistent button, and it opens a centered
            popup. Two entry points for one search was the thing C2 set out to remove. */}

        {/* Nav — the three designed sections from the ONE nav model (nav.ts). */}
        <nav className="flex-1 overflow-y-auto px-3 py-2">
          {navSections.map((section, idx) => (
            <Section
              key={section.title}
              section={section}
              path={path}
              collapsed={collapsed}
              counts={counts}
              className={idx > 0 ? 'mt-4' : undefined}
            />
          ))}
        </nav>

        {/* Footer */}
        <div className="space-y-1 border-t border-sidebar-border px-3 py-3">
          <button
            onClick={toggle}
            className={cn(
              'flex w-full items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent/60',
              collapsed && 'justify-center px-2',
            )}
            title={collapsed ? (theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus') : undefined}
          >
            {theme === 'dark' ? <Sun className="size-4" /> : <Moon className="size-4" />}
            {!collapsed && (theme === 'dark' ? 'Heller Modus' : 'Dunkler Modus')}
          </button>
          <a
            href="#"
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent/60',
              collapsed && 'justify-center px-2',
            )}
            title={collapsed ? 'Support' : undefined}
          >
            <LifeBuoy className="size-4" />
            {!collapsed && 'Support'}
          </a>
          <a
            href="#"
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm text-sidebar-foreground/80 hover:bg-sidebar-accent/60',
              collapsed && 'justify-center px-2',
            )}
            title={collapsed ? 'Feedback' : undefined}
          >
            <MessageSquarePlus className="size-4" />
            {!collapsed && 'Feedback'}
          </a>
          {/* Part C: the profile block opens the user-settings page (/einstellungen).
              Abmelden stays a sibling button (can't nest a button inside the Link). */}
          {!collapsed && (
            <div className="mt-2 flex items-center gap-2 rounded-md px-2 py-1.5">
              <Link
                to="/einstellungen"
                title="Einstellungen"
                className="flex min-w-0 flex-1 items-center gap-2 rounded-md py-0.5 hover:bg-sidebar-accent/60"
              >
                <ProfileAvatar avatarUrl={avatarUrl} initials={session?.initials ?? '?'} />
                <div className="min-w-0 flex-1 text-left">
                  <div className="truncate text-sm font-medium text-sidebar-foreground">
                    {session?.displayName ?? session?.email ?? 'Angemeldet'}
                  </div>
                  <div className="truncate text-[10px] text-sidebar-foreground/50">
                    {session?.email ?? ''}
                  </div>
                </div>
              </Link>
              <button
                aria-label="Abmelden"
                title="Abmelden"
                onClick={() => logout()}
                className="text-sidebar-foreground/40 hover:text-sidebar-foreground"
              >
                <LogOut className="size-4" />
              </button>
            </div>
          )}
          {collapsed && (
            <div className="mt-2 flex flex-col items-center gap-2">
              <Link
                to="/einstellungen"
                aria-label="Einstellungen"
                title="Einstellungen"
                className="block rounded-full hover:opacity-80"
              >
                <ProfileAvatar avatarUrl={avatarUrl} initials={session?.initials ?? '?'} />
              </Link>
              <button
                aria-label="Abmelden"
                title="Abmelden"
                onClick={() => logout()}
                className="text-sidebar-foreground/40 hover:text-sidebar-foreground"
              >
                <LogOut className="size-4" />
              </button>
            </div>
          )}
        </div>
      </aside>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="flex h-12 shrink-0 items-center gap-2 border-b border-border px-4">
          <button
            aria-label={sidebarToggleLabel}
            title={sidebarToggleTitle}
            aria-expanded={!collapsed}
            aria-controls="app-sidebar"
            onClick={toggleSidebar}
            className="text-muted-foreground hover:text-foreground"
          >
            {collapsed ? (
              <PanelLeftOpen className="size-4" />
            ) : (
              <PanelLeftClose className="size-4" />
            )}
          </button>
          {/* Back/forward drive real router history; each is disabled (greyed, no hover)
              when there's nowhere to go that way. */}
          <button
            aria-label="Zurück"
            title="Zurück"
            onClick={() => router.history.back()}
            disabled={!canGoBack}
            aria-disabled={!canGoBack}
            className={cn(
              'grid size-6 place-items-center rounded-md',
              canGoBack
                ? 'text-muted-foreground hover:bg-accent hover:text-foreground'
                : 'cursor-default text-muted-foreground/40',
            )}
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            aria-label="Vorwärts"
            title="Vorwärts"
            onClick={() => router.history.forward()}
            disabled={!canGoForward}
            aria-disabled={!canGoForward}
            className={cn(
              'grid size-6 place-items-center rounded-md',
              canGoForward
                ? 'text-muted-foreground hover:bg-accent hover:text-foreground'
                : 'cursor-default text-muted-foreground/40',
            )}
          >
            <ChevronRight className="size-4" />
          </button>
          <span className="ml-1 text-sm font-medium">{titleFor(path)}</span>
          {/* S4: the ONE global search entry, moved to the LEFT of the header (was in the
              right cluster). It is always the combined keyword+semantic search — no mode
              toggle. (The Ablage-page search keeps its own full-text/semantic toggle; only
              this global bar is always-combined.) */}
          <HeaderSearchTrigger onOpen={() => setSearchOpen(true)} />
          <div className="flex-1" />
          {/* S5: global Upload — opens the shared UploadDocumentDialog from any page.
              Permission-gated on `document.upload` (V4.0 Default Deny — the same gate the
              Ablage page's upload button uses); hidden entirely for users without it. */}
          {adminAccess.can('document.upload') && (
            <button
              type="button"
              onClick={() => setUploadOpen(true)}
              aria-label="Dokument hochladen"
              title="Dokument hochladen"
              className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-muted-foreground transition-colors hover:bg-accent hover:text-foreground"
            >
              <Upload className="size-4" />
              <span className="hidden sm:inline">Hochladen</span>
            </button>
          )}
          {/* KI-Assistent (FIX 4): global entry to Brok, immediately left of the bell.
              Brok brand periwinkle accent (NOT the VJI green); Mantaray icon from features/brok. */}
          <Link
            to="/brok"
            aria-label="KI-Assistent öffnen"
            title="KI-Assistent"
            className="inline-flex items-center gap-1.5 rounded-md px-2.5 py-1.5 text-xs font-medium text-primary transition-colors hover:bg-primary/10"
          >
            <BrokRay size={16} />
            <span className="hidden sm:inline">KI-Assistent</span>
          </Link>
          {/* Part D: the in-app notification bell (unread badge + dropdown). */}
          <NotificationBell />
        </header>
        <main className="flex-1 overflow-y-auto">{children}</main>
        {searchOpen && <GlobalSearchDialog onClose={() => setSearchOpen(false)} />}
        <UploadDocumentDialog
          open={uploadOpen}
          onOpenChange={setUploadOpen}
          onUpload={handleGlobalUpload}
        />
        {uploadToast && (
          <Toast
            tone={uploadToast === 'dup' ? 'alert' : 'ok'}
            onDismiss={() => setUploadToast(null)}
          >
            {uploadToast === 'dup'
              ? 'Duplikat erkannt: Dokument bereits vorhanden.'
              : 'Dokument hochgeladen, wird analysiert …'}
          </Toast>
        )}
      </div>
    </div>
  );
}
